import dayjs from 'dayjs';

import { OpsiumUserShortResponse, RateSource } from '@/modules/common';
import { MemberDailyUtilizationResponse } from '@/modules/planner/types/dto/response/member-daily-utilization-response';

import { AssignmentRate } from './assignment-rate';
import { AssignmentStatus } from './assignment-status';
import { AssignmentResponse, PlannerUserStateChangeResponse } from './dto';
import { PlannerAssignmentProjectResponse } from './dto/response/planner-assignment-project-response';
import { PlannerCustomAttributeResponse } from './dto/response/planner-custom-attribute-response';
import {
  PlannerTimeoffResponse,
  TimeOffRequestStatusDTO,
} from './dto/response/planner-timeoff-response';

export enum RowItemType {
  Group = 'group',
  User = 'user',
}

export type RowItem = {
  id: string;
  title: string;
  hoursPerDay: number;
  type: RowItemType;
  color?: string;
  user?: OpsiumUserShortResponse;
  stateChanges?: PlannerUserStateChangeResponse[];
  /** The member's custom attribute values (user rows only) */
  customAttributes?: PlannerCustomAttributeResponse[];
  utilizationByDate?: Map<string, MemberDailyUtilizationResponse>;
  parentGroupId?: string;
  /** Dates where group-level utilization data is unavailable (backfilled members only) */
  noDataDates?: Set<string>;
};

// TODO: take into account proper naming of types and interfaces
export type AssignmentType = {
  id: AssignmentResponse['id'];
  /** Reference to original API response data */
  originalAssignment: AssignmentResponse;
  /**
   * Planner-only project data for this assignment (client name, tentative
   * probability, status info). Undefined for assignments not sourced from a
   * planner response (e.g. optimistic entries).
   */
  plannerProject?: PlannerAssignmentProjectResponse;
  /** Derived fields for UI convenience */
  title: string;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  color: string;
  hoursPerDay: number;
  status: AssignmentStatus;
  probability: number | null;
  /** Date ranges when the assignment is active (e.g., excludes weekends for doSkipWeekdays assignments) */
  activePeriods: Array<{
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
    /** Rate info for this period; null only when the very first period had no rate */
    rate: AssignmentRate | null;
    /** Date when this rate became effective; null when rate is null */
    rateChangeDate: dayjs.Dayjs | null;
  }>;
  /** Whether distinct rate values exist across active periods */
  hasRateChanges: boolean;
  /** Whether the assignment doesn't match the requested filters */
  isFilteredOut: boolean;
  /** Whether the assignment belongs to the planner group */
  belongsToTheGroup: boolean;
  /** UI-only field to indicate if assignment is disabled for interaction */
  disabled?: boolean;
  /** Booked minutes over the whole period, net of approved time off; undefined when unknown */
  bookedMinutes?: number;
  /** Virtual adjustment of minutesPerDay on dates with time-off overlap. Key is YYYY-MM-DD. */
  adjustedMinutesByDate?: Record<string, number>;
};

export type SelectedDatesType = {
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
};

export type RateOption = {
  value: RateSource;
  label: string;
  disabled: boolean;
  rate: number | undefined;
  currency: string;
};

export interface VisibleDateRange {
  startIndex: number;
  endIndex: number;
}

export enum ResizeDirection {
  Forward = 'forward',
  Backward = 'backward',
}

export type TimeOffBlockType = {
  id: number;
  originalTimeOff: PlannerTimeoffResponse;
  title: string;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  status: TimeOffRequestStatusDTO;
  totalMinutes: number;
};
